import { config } from '../../utils/utils';
import store from '../../store';
import {
  AdvertiseEditNotificationResponse,
  UnreadCountResponse,
} from './types';

/**
 * Fetch advertise edit notifications from the API
 * @param options Optional parameters including pagination and filters
 * @returns Promise with notifications data
 */
export const fetchAdvertiseEditNotifications = async (
  options?: {
    skip?: number;
    limit?: number;
    isRead?: boolean | string;
    receiverRole?: 'company' | 'admin';
    advertiseId?: string;
  }
): Promise<AdvertiseEditNotificationResponse> => {
  try {
    const { accessToken } = store.getState().auth;

    let url = `${config.BASE_URL}/advertise-edit-notification`;
    const params = new URLSearchParams();

    if (options?.skip !== undefined) {
      params.append('skip', options.skip.toString());
    }

    if (options?.limit !== undefined) {
      params.append('limit', options.limit.toString());
    }

    if (options?.isRead !== undefined) {
      params.append('isRead', options.isRead.toString());
    }

    if (options?.receiverRole) {
      params.append('receiverRole', options.receiverRole);
    }

    if (options?.advertiseId) {
      params.append('advertiseId', options.advertiseId);
    }

    const queryString = params.toString();
    if (queryString) {
      url = `${url}?${queryString}`;
    }

    const result = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!result.ok) {
      throw new Error('Failed to fetch notifications');
    }

    return await result.json();
  } catch (error) {
    console.error('Error fetching notifications:', error);
    throw error;
  }
};

/**
 * Fetch unread notification count
 * @returns Promise with unread count
 */
export const fetchUnreadCount = async (): Promise<UnreadCountResponse> => {
  try {
    const { accessToken } = store.getState().auth;

    const url = `${config.BASE_URL}/advertise-edit-notification-unread-count`;

    const result = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!result.ok) {
      throw new Error('Failed to fetch unread count');
    }

    return await result.json();
  } catch (error) {
    console.error('Error fetching unread count:', error);
    throw error;
  }
};

/**
 * Mark a notification as read
 * @param notificationId Notification ID to mark as read
 * @returns Promise with updated notification
 */
export const markNotificationAsRead = async (
  notificationId: string
): Promise<any> => {
  try {
    const { accessToken } = store.getState().auth;

    const url = `${config.BASE_URL}/advertise-edit-notification/${notificationId}`;

    const result = await fetch(url, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        isRead: true,
      }),
    });

    if (!result.ok) {
      throw new Error('Failed to mark notification as read');
    }

    return await result.json();
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

/**
 * Mark all notifications as read
 * @returns Promise
 */
export const markAllAsRead = async (): Promise<void> => {
  try {
    // First, get all unread notifications
    const response = await fetchAdvertiseEditNotifications({
      isRead: false,
      limit: 1000, // Get all unread notifications
    });

    // Mark each as read
    const promises = response.data.map((notification) =>
      markNotificationAsRead(notification.id)
    );

    await Promise.all(promises);
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw error;
  }
};

